// feat-297 — hooks behind <PinnedHeaderTable>. Kept separate from the component
// so each can be unit-tested on its own (jsdom has no layout, so the tests stub
// getBoundingClientRect / ResizeObserver and drive these directly).
import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * How the pinned header finds `ScorecardNavigation` without touching it.
 *
 * The spec forbids changing the nav, so it carries no id/data hook. Its root is
 * the only element in the app whose class list is `sticky top-0 z-50`
 * (`ScorecardNavigation.tsx:304`; grep `sticky top-0` — every other hit is
 * z-10 or has no z). If the nav's classes ever change, this falls back to a 0px
 * offset (header pins at the viewport top, hidden behind the nav) — the unit
 * test `useNavHeight` pins this selector against the nav's literal class string
 * so that drift fails CI instead.
 */
export const SCORECARD_NAV_SELECTOR = '.sticky.top-0.z-50';

/**
 * Live height of the sticky nav, in CSS px (fractional — the nav measures e.g.
 * 85.5px at 1280). This is the pinned header's `top`.
 *
 * `getBoundingClientRect().height` is the border-box, so it includes the nav's
 * 1px `border-b`: the header's top edge lands flush on the nav's bottom edge
 * (design NOTES §1b: 0px gap, 0px overlap). A ResizeObserver tracks the height
 * as the welcome/role line wraps or the < lg tab row appears; `resize` is the
 * fallback where ResizeObserver is absent.
 */
export function useNavHeight(selector: string = SCORECARD_NAV_SELECTOR, enabled = true): number {
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const nav = document.querySelector<HTMLElement>(selector);
    if (!nav) {
      setHeight(0);
      return undefined;
    }
    const measure = () => setHeight(nav.getBoundingClientRect().height);
    measure();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(nav);
    }
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [selector, enabled]);

  return height;
}

/**
 * Keeps the header strip's `scrollLeft` equal to the body scroller's.
 *
 * `scrollLeft`, never `transform` (design NOTES §1d): the frozen SERVER corner
 * cell is `sticky left-0`, which only holds relative to a *scrolling* ancestor.
 * The strip is `overflow-hidden` — a scroll container the user cannot scroll
 * directly — so body → strip is the primary direction. strip → body covers the
 * cases where the browser scrolls the strip itself (focus, find-in-page), and a
 * horizontal wheel/trackpad gesture over the pinned header is forwarded to the
 * body so it feels like one surface. Both assignments are guarded by an
 * equality check, so the two listeners cannot ping-pong.
 */
export function useSyncedScrollLeft(
  stripRef: RefObject<HTMLElement | null>,
  bodyRef: RefObject<HTMLElement | null>,
  enabled = true,
): () => void {
  const sync = useCallback(() => {
    const strip = stripRef.current;
    const body = bodyRef.current;
    if (strip && body && strip.scrollLeft !== body.scrollLeft) strip.scrollLeft = body.scrollLeft;
  }, [stripRef, bodyRef]);

  useEffect(() => {
    if (!enabled) return undefined;
    const strip = stripRef.current;
    const body = bodyRef.current;
    if (!strip || !body) return undefined;
    const onBody = () => {
      if (strip.scrollLeft !== body.scrollLeft) strip.scrollLeft = body.scrollLeft;
    };
    const onStrip = () => {
      if (body.scrollLeft !== strip.scrollLeft) body.scrollLeft = strip.scrollLeft;
    };
    const onWheel = (e: WheelEvent) => {
      if (e.deltaX !== 0 && Math.abs(e.deltaX) > Math.abs(e.deltaY)) body.scrollLeft += e.deltaX;
    };
    body.addEventListener('scroll', onBody, { passive: true });
    strip.addEventListener('scroll', onStrip, { passive: true });
    strip.addEventListener('wheel', onWheel, { passive: true });
    onBody();
    return () => {
      body.removeEventListener('scroll', onBody);
      strip.removeEventListener('scroll', onStrip);
      strip.removeEventListener('wheel', onWheel);
    };
  }, [stripRef, bodyRef, enabled]);

  return sync;
}

export interface HeaderGeometry {
  /** Border-box width of each column, left to right, measured from the body table's own thead. */
  colWidths: number[];
  /** Border-box width of the body table (the header table is set to exactly this). */
  tableWidth: number;
  /** Height of the body table's thead — how far the body table is pulled up under the strip. */
  headHeight: number;
}

const EMPTY_GEOMETRY: HeaderGeometry = { colWidths: [], tableWidth: 0, headHeight: 0 };

const sameGeometry = (a: HeaderGeometry, b: HeaderGeometry) =>
  a.tableWidth === b.tableWidth &&
  a.headHeight === b.headHeight &&
  a.colWidths.length === b.colWidths.length &&
  a.colWidths.every((w, i) => w === b.colWidths[i]);

/** Reads the geometry off a laid-out body table. Exported for tests. */
export function measureHeaderGeometry(table: HTMLTableElement): HeaderGeometry {
  const head = table.tHead;
  if (!head) return EMPTY_GEOMETRY;
  const lastRow = head.rows[head.rows.length - 1];
  const colWidths = lastRow
    ? Array.from(lastRow.cells).map((c) => c.getBoundingClientRect().width)
    : [];
  return {
    colWidths,
    tableWidth: table.getBoundingClientRect().width,
    headHeight: head.getBoundingClientRect().height,
  };
}

/**
 * Column widths for the pinned header, taken from the body table.
 *
 * Why measure rather than share a `table-layout: fixed` + hard-coded widths:
 * every one of these tables is auto-layout today, and their column widths come
 * from the content (clamped labels, `min-w-min`, long server names, the dense
 * branch's `w-max`). Hard-coding widths would change the at-rest look, which the
 * design forbids. So the BODY table keeps its real `<thead>` and lays out
 * exactly as it does on develop; we read each header cell's border-box width
 * and give the header table `table-layout: fixed` + a `<colgroup>` with those
 * widths + the same total width. The header copy therefore cannot wrap or size
 * differently from the columns under it. Re-measured on every render (cheap:
 * one rect per column) and whenever the body table resizes (viewport change,
 * web-font swap).
 */
export function useHeaderGeometry(
  tableRef: RefObject<HTMLTableElement | null>,
  enabled = true,
): HeaderGeometry {
  const [geometry, setGeometry] = useState<HeaderGeometry>(EMPTY_GEOMETRY);
  const latest = useRef(geometry);
  latest.current = geometry;

  const measure = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;
    const next = measureHeaderGeometry(table);
    if (!sameGeometry(latest.current, next)) setGeometry(next);
  }, [tableRef]);

  // No deps on purpose: data changes can redistribute column widths without
  // resizing the table, and a render is exactly when that happens.
  useLayoutEffect(() => {
    if (enabled) measure();
  });

  useEffect(() => {
    if (!enabled || typeof ResizeObserver === 'undefined') return undefined;
    const table = tableRef.current;
    if (!table) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(table);
    if (table.tHead) ro.observe(table.tHead);
    return () => ro.disconnect();
  }, [tableRef, measure, enabled]);

  return geometry;
}

/**
 * True while the strip is stuck under the nav, i.e. displaced from where it
 * would sit in flow (the top of its wrapper). Drives `data-pinned`, which the
 * design hangs the shadow / squared corners on. Checked on window scroll
 * (rAF-throttled), resize, and whenever the pin offset changes.
 */
export function usePinnedState(
  stripRef: RefObject<HTMLElement | null>,
  wrapperRef: RefObject<HTMLElement | null>,
  top: number,
  enabled = true,
): boolean {
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let frame = 0;
    const check = () => {
      frame = 0;
      const strip = stripRef.current;
      const wrapper = wrapperRef.current;
      if (!strip || !wrapper) return;
      setPinned(strip.getBoundingClientRect().top - wrapper.getBoundingClientRect().top > 0.5);
    };
    const schedule = () => {
      if (frame) return;
      frame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(check) : 0;
      if (!frame) check();
    };
    check();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [stripRef, wrapperRef, top, enabled]);

  return pinned;
}
